import { useMutation, useQuery } from "@tanstack/react-query";
import { useEffect, useRef, useState } from "react";
import { compareBudgetVersions, fetchBudgetEditor, searchComparisonOptions } from "../repo/quoteBudgetRepo";
import type { QuoteBudgetComparisonItinerary, QuoteBudgetVersion } from "../types";

/** 比较页每侧已选行程及其版本。 */
export type ComparisonSelection = { itinerary: QuoteBudgetComparisonItinerary; version: QuoteBudgetVersion };
type Side = "left" | "right";

/** 金额以字符串展示，保留两位小数及大额整数精度。 */
export function comparisonMoney(amount: string): string {
  const [integer, fraction = ""] = amount.split(".");
  return `¥${integer.replace(/\B(?=(\d{3})+(?!\d))/g, ",")}.${fraction.padEnd(2, "0").slice(0, 2)}`;
}

/** 按分转换为整数，避免金额差值产生浮点误差。 */
function amountCents(value: string): bigint {
  const [integer, fraction = ""] = value.replace(/^-/, "").split(".");
  const cents = BigInt(integer || "0") * 100n + BigInt(fraction.padEnd(2, "0").slice(0, 2));
  return value.startsWith("-") ? -cents : cents;
}

/** 左右搜索独立防抖，查询 key 隔离关键词与旧响应。 */
export function useComparisonSearch() {
  const [input, setInput] = useState("");
  const [query, setQuery] = useState("");
  useEffect(() => {
    const timer = window.setTimeout(() => setQuery(input.trim()), 300);
    return () => window.clearTimeout(timer);
  }, [input]);
  const result = useQuery({
    queryKey: ["quote-budget-comparison-options", query],
    queryFn: ({ signal }) => searchComparisonOptions(query, signal),
    enabled: Boolean(query), refetchOnWindowFocus: false, retry: false,
  });
  const current = input.trim() === query;
  return {
    search: setInput, input,
    items: current ? result.data?.items ?? [] : [],
    limited: current && result.data?.limited,
    loading: Boolean(input.trim()) && (!current || result.isFetching),
    failed: current && result.isError, retry: () => void result.refetch(),
  };
}

/** 管理独立版本比较页的默认版本、选择身份和异步摘要。 */
export function useQuoteBudgetVersionCompareRuntime(budgetOrderId: string) {
  const editor = useQuery({
    queryKey: ["quote-budget-comparison-editor", budgetOrderId],
    queryFn: () => fetchBudgetEditor(budgetOrderId),
    enabled: Boolean(budgetOrderId), retry: false, refetchOnWindowFocus: false,
  });
  const [selections, setSelections] = useState<Partial<Record<Side, ComparisonSelection>>>({});
  const generation = useRef(0);
  const submitting = useRef(false);
  const data = editor.data;
  const current: QuoteBudgetComparisonItinerary | undefined = data ? {
    itineraryId: data.itinerarySummary?.itineraryId || data.budgetOrder.routeId,
    itineraryTitle: data.itinerarySummary?.title || data.budgetOrder.routeId,
    startDate: data.itinerarySummary?.startDate, endDate: data.itinerarySummary?.endDate,
    versions: [...data.versions].sort((a, b) => a.versionNo - b.versionNo),
  } : undefined;
  const first = current?.versions.find((v) => v.versionNo === 1) ?? current?.versions[0];
  const latest = current?.versions.at(-1);
  const left = selections.left ?? (current && first ? { itinerary: current, version: first } : undefined);
  const right = selections.right ?? (current && latest ? { itinerary: current, version: latest } : undefined);
  const pair = `${left?.version.budgetOrderId}|${right?.version.budgetOrderId}`;
  const mutation = useMutation({
    mutationFn: async (request: { left: string; right: string; pair: string; generation: number }) => {
      try {
        return { pair: request.pair, generation: request.generation, result: await compareBudgetVersions(request.left, request.right) };
      } finally {
        submitting.current = false;
      }
    },
    retry: false,
  });
  /** 选择变化后撤销上一次摘要的展示资格。 */
  function invalidateSummary() { generation.current += 1; mutation.reset(); }
  /** 新行程自动选择首版或最新版本。 */
  function selectItinerary(side: Side, itinerary: QuoteBudgetComparisonItinerary) {
    const versions = [...itinerary.versions].sort((a, b) => a.versionNo - b.versionNo);
    const version = side === "left" ? versions.find((v) => v.versionNo === 1) ?? versions[0] : versions.at(-1);
    if (!version) return;
    invalidateSummary();
    setSelections((previous) => ({ ...previous, [side]: { itinerary, version } }));
  }
  /** 版本始终限于已选行程的候选集合。 */
  function selectVersion(side: Side, id: string) {
    const selection = side === "left" ? left : right;
    const version = selection?.itinerary.versions.find((v) => v.budgetOrderId === id);
    if (!selection || !version) return;
    invalidateSummary();
    setSelections((previous) => ({ ...previous, [side]: { itinerary: selection.itinerary, version } }));
  }
  const sameVersion = Boolean(left && right && left.version.budgetOrderId === right.version.budgetOrderId);
  const generated = mutation.data?.pair === pair && mutation.data.generation === generation.current ? mutation.data.result : undefined;
  const leftAmount = generated?.left?.budgetOrder.costBudgetTotalAmount ?? left?.version.costBudgetTotalAmount;
  const rightAmount = generated?.right?.budgetOrder.costBudgetTotalAmount ?? right?.version.costBudgetTotalAmount;
  const delta = leftAmount !== undefined && rightAmount !== undefined ? amountCents(rightAmount) - amountCents(leftAmount) : undefined;
  const absolute = delta !== undefined && delta < 0n ? -delta : delta;
  const deltaMoney = absolute === undefined ? "" : comparisonMoney(`${absolute / 100n}.${String(absolute % 100n).padStart(2, "0")}`);
  const deltaText = delta === undefined ? "请选择两个版本" : delta === 0n ? "总费用无变化" : `${delta > 0n ? "增加" : "减少"} ${deltaMoney}`;
  /** 提交当前组合并在同步边界防止重复点击。 */
  function generate() {
    if (!left || !right || sameVersion || submitting.current) return;
    submitting.current = true;
    mutation.mutate({ left: left.version.budgetOrderId, right: right.version.budgetOrderId, pair, generation: generation.current });
  }
  return {
    current, left, right, selectItinerary, selectVersion, sameVersion, generate,
    summary: generated?.summary ?? "", error: mutation.isError ? "差异说明生成失败，请稍后重试。" : "",
    loading: editor.isLoading, loadError: editor.isError, retry: () => void editor.refetch(),
    generating: mutation.isPending, canGenerate: Boolean(left && right && !sameVersion && !editor.isError),
    deltaText, amountIncreased: delta !== undefined && delta > 0n,
    rightAmount: rightAmount === undefined ? "—" : comparisonMoney(rightAmount),
  };
}
